import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, MagnifyingGlass, Export, Upload, ArrowsLeftRight, UserSwitch, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STAGES = ['new_inquiry', 'contacted', 'qualified', 'demo_scheduled', 'demo_attended', 'application', 'fee_pending', 'enrolled', 'lost', 'on_hold'];
const SOURCES = ['Meta Ads', 'Google Ads', 'Website', 'WhatsApp', 'Walk-in', 'Referral', 'School Tie-up', 'Event', 'Call', 'Just Dial'];

const STAGE_ACCENT = {
  new_inquiry: '#2B6EF0', contacted: '#06B6D4', qualified: '#8B5CF6',
  demo_scheduled: '#E5A100', demo_attended: '#F97316', application: '#6366F1',
  fee_pending: '#EAB308', enrolled: '#10A882', lost: '#EF4444', on_hold: '#808080',
};

export default function LeadListPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ student_name: '', parent_name: '', parent_phone: '', email: '', source: 'manual', grade: '', target_exam: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStage, setBulkStage] = useState('');
  const [showBulkTransfer, setShowBulkTransfer] = useState(false);
  const [showBulkReassign, setShowBulkReassign] = useState(false);
  const [reassignCounselors, setReassignCounselors] = useState([]);
  const [reassignCounselorId, setReassignCounselorId] = useState('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (stageFilter !== 'all') params.stage = stageFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (campaignFilter !== 'all') params.campaign_id = campaignFilter;
      const { data } = await api.get('/api/leads', { params });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, stageFilter, sourceFilter, campaignFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    api.get('/api/campaigns').then((r) => {
      setCampaigns(r.data.campaigns || r.data || []);
    }).catch(() => {});
  }, []);

  // initialize campaign filter from URL ?campaign_id=
  useEffect(() => {
    const cid = searchParams.get('campaign_id');
    if (cid && cid !== campaignFilter) setCampaignFilter(cid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleCampaignChange = (val) => {
    setCampaignFilter(val);
    const next = new URLSearchParams(searchParams);
    if (val === 'all') next.delete('campaign_id');
    else next.set('campaign_id', val);
    setSearchParams(next, { replace: true });
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (stageFilter !== 'all') params.stage = stageFilter;
      const { data } = await api.get('/api/leads/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Leads exported');
    } catch (err) { toast.error('Failed to export leads'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/api/leads/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${data.created} leads. Skipped ${data.skipped} duplicates.`);
      if (data.errors?.length) {
        toast.error(`${data.errors.length} rows had errors`);
      }
      fetchLeads();
    } catch (err) { toast.error('Failed to import leads'); }
    e.target.value = '';
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/leads', form);
      toast.success('Lead created');
      setShowCreate(false);
      setForm({ student_name: '', parent_name: '', parent_phone: '', email: '', source: 'manual', grade: '', target_exam: '' });
      fetchLeads();
    } catch (err) { toast.error('Failed to create lead'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === leads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(leads.map(l => l.id));
    }
  };

  const handleBulkStageChange = async () => {
    if (!bulkStage || selectedIds.length === 0) return;
    try {
      const { data } = await api.put('/api/leads/bulk/stage', { lead_ids: selectedIds, stage: bulkStage });
      toast.success(`Updated ${data.modified} leads to ${bulkStage.replace('_', ' ')}`);
      setSelectedIds([]);
      setBulkStage('');
      fetchLeads();
    } catch (err) { toast.error('Bulk update failed'); }
  };

  const handleOpenReassign = async () => {
    try {
      const { data } = await api.get('/api/users?role=counselor');
      setReassignCounselors(data.users || data || []);
      setShowBulkReassign(true);
    } catch (err) { toast.error('Failed to load counselors'); }
  };

  const handleBulkReassign = async () => {
    if (!reassignCounselorId || selectedIds.length === 0) return;
    try {
      const { data } = await api.put('/api/leads/reassign', { lead_ids: selectedIds, counselor_id: reassignCounselorId });
      toast.success(`Reassigned ${data.reassigned} leads`);
      setSelectedIds([]);
      setReassignCounselorId('');
      setShowBulkReassign(false);
      fetchLeads();
    } catch (err) { toast.error('Reassignment failed'); }
  };

  return (
    <div data-testid="lead-list-page" className="stagger-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="font-mono">{total}</span> total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-leads-btn" variant="outline" className="h-9 rounded-lg text-xs gap-1.5 font-semibold" onClick={handleExport}>
            <Export size={14} weight="bold" /> Export CSV
          </Button>
          <Button data-testid="import-leads-btn" variant="outline" className="h-9 rounded-lg text-xs gap-1.5 font-semibold" onClick={() => document.getElementById('csv-import-input').click()}>
            <Upload size={14} weight="bold" /> Import
          </Button>
          <input id="csv-import-input" type="file" accept=".csv" className="hidden" onChange={handleImport} />
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-lead-btn" className="h-9 rounded-lg text-xs gap-1.5 font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>
              <Plus size={14} weight="bold" /> Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl max-w-md">
            <DialogHeader><DialogTitle className="font-display text-foreground text-lg">Add New Lead</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Student Name *</Label><Input data-testid="new-lead-name" value={form.student_name} onChange={e => setForm({...form, student_name: e.target.value})} className="h-9 rounded-lg text-sm" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Parent Name</Label><Input data-testid="new-lead-parent" value={form.parent_name} onChange={e => setForm({...form, parent_name: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Phone</Label><Input data-testid="new-lead-phone" value={form.parent_phone} onChange={e => setForm({...form, parent_phone: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Email</Label><Input data-testid="new-lead-email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="h-9 rounded-lg text-sm" /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Source</Label>
                  <select data-testid="new-lead-source" value={form.source} onChange={e => setForm({...form, source: e.target.value})} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
                    {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                    <option value="manual">Manual Entry</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Grade</Label><Input data-testid="new-lead-grade" value={form.grade} onChange={e => setForm({...form, grade: e.target.value})} className="h-9 rounded-lg text-sm" placeholder="e.g., 11" /></div>
                <div><Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Target Exam</Label><Input data-testid="new-lead-exam" value={form.target_exam} onChange={e => setForm({...form, target_exam: e.target.value})} className="h-9 rounded-lg text-sm" placeholder="e.g., JEE" /></div>
              </div>
              <Button type="submit" data-testid="submit-lead-btn" className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>Create Lead</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="lead-filters">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search by name, phone, email..." value={search} onChange={e => setSearch(e.target.value)} data-testid="lead-search" className="pl-9 h-9 rounded-lg text-xs" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger data-testid="stage-filter" className="w-36 h-9 text-xs rounded-lg"><SelectValue placeholder="All Stages" /></SelectTrigger>
          <SelectContent>{[<SelectItem key="all" value="all">All Stages</SelectItem>, ...STAGES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)]}</SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger data-testid="source-filter" className="w-32 h-9 text-xs rounded-lg"><SelectValue placeholder="All Sources" /></SelectTrigger>
          <SelectContent>{[<SelectItem key="all" value="all">All Sources</SelectItem>, ...SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)]}</SelectContent>
        </Select>
        <select
          value={campaignFilter}
          onChange={(e) => handleCampaignChange(e.target.value)}
          data-testid="campaign-filter"
          className="h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono max-w-[180px]"
        >
          <option value="all">All Campaigns</option>
          {campaigns.map((c) => (
            <option key={c.id || c._id} value={c.id || c._id}>{c.name || c.id || c._id}</option>
          ))}
        </select>
      </div>

      {/* Bulk Action Toolbar */}
      {selectedIds.length > 0 && (
        <div data-testid="bulk-action-toolbar" className="flex items-center gap-3 mb-4 p-3 bg-secondary/70 border border-border rounded-lg">
          <span className="text-xs font-mono text-muted-foreground">{selectedIds.length} selected</span>
          <Select value={bulkStage} onValueChange={setBulkStage}>
            <SelectTrigger data-testid="bulk-stage-select" className="w-44 h-8 text-xs rounded-lg">
              <SelectValue placeholder="Change Stage..." />
            </SelectTrigger>
            <SelectContent>
              {STAGES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="bulk-stage-apply" size="sm" className="h-8 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#2563EB' }} onClick={handleBulkStageChange} disabled={!bulkStage}>
            Apply
          </Button>
          <div className="w-px h-5 bg-border mx-1" />
          <Button data-testid="bulk-transfer-btn" variant="outline" size="sm" className="h-8 rounded-lg text-xs gap-1" onClick={() => setShowBulkTransfer(true)}>
            <ArrowsLeftRight size={12} /> Transfer to Branch
          </Button>
          <Button data-testid="bulk-reassign-btn" variant="outline" size="sm" className="h-8 rounded-lg text-xs gap-1" onClick={handleOpenReassign}>
            <UserSwitch size={12} /> Reassign Counselor
          </Button>
          <Button data-testid="bulk-clear" variant="ghost" size="sm" className="h-8 rounded-lg text-xs" onClick={() => setSelectedIds([])}>
            Clear
          </Button>
        </div>
      )}

      {/* Bulk Transfer Dialog */}
      {showBulkTransfer && (
        <BulkTransferDialog
          leadIds={selectedIds}
          onClose={() => setShowBulkTransfer(false)}
          onTransferred={() => { setShowBulkTransfer(false); setSelectedIds([]); fetchLeads(); }}
        />
      )}

      {/* Bulk Reassign Dialog */}
      {showBulkReassign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="bulk-reassign-dialog">
          <div className="bg-card border border-border rounded-xl w-full max-w-sm p-5 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold text-foreground">Reassign {selectedIds.length} Lead{selectedIds.length > 1 ? 's' : ''}</h2>
              <button onClick={() => setShowBulkReassign(false)} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Counselor *</label>
                <select value={reassignCounselorId} onChange={e => setReassignCounselorId(e.target.value)} data-testid="reassign-counselor-select"
                  className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono">
                  <option value="">Select counselor...</option>
                  {reassignCounselors.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setShowBulkReassign(false)} className="h-8 rounded-lg text-xs">Cancel</Button>
                <Button size="sm" disabled={!reassignCounselorId} data-testid="reassign-submit-btn" onClick={handleBulkReassign}
                  className="h-8 rounded-lg text-xs font-bold px-4 text-white" style={{ backgroundColor: '#2563EB' }}>
                  Reassign
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-border rounded-xl bg-card overflow-hidden" data-testid="leads-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead className="w-10 h-9">
                <input
                  type="checkbox"
                  data-testid="select-all-leads"
                  checked={leads.length > 0 && selectedIds.length === leads.length}
                  onChange={toggleSelectAll}
                  className="rounded-sm border-border"
                />
              </TableHead>
              <TableHead className="text-[10px] font-bold text-muted-foreground h-9 uppercase tracking-wider">Student</TableHead>
              <TableHead className="text-[10px] font-bold text-muted-foreground h-9 uppercase tracking-wider">Parent / Phone</TableHead>
              <TableHead className="text-[10px] font-bold text-muted-foreground h-9 uppercase tracking-wider">Stage</TableHead>
              <TableHead className="text-[10px] font-bold text-muted-foreground h-9 uppercase tracking-wider">Score</TableHead>
              <TableHead className="text-[10px] font-bold text-muted-foreground h-9 uppercase tracking-wider">Source</TableHead>
              <TableHead className="text-[10px] font-bold text-muted-foreground h-9 uppercase tracking-wider">Exam</TableHead>
              <TableHead className="text-[10px] font-bold text-muted-foreground h-9 uppercase tracking-wider">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center text-xs text-muted-foreground h-32 font-mono">loading...</TableCell></TableRow>
            ) : leads.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-xs text-muted-foreground h-32">No leads found</TableCell></TableRow>
            ) : leads.map(lead => (
              <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`} className="cursor-pointer hover:bg-secondary/40 transition-colors">
                <TableCell className="w-10 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    data-testid={`select-lead-${lead.id}`}
                    checked={selectedIds.includes(lead.id)}
                    onChange={() => toggleSelect(lead.id)}
                    className="rounded-sm border-border"
                  />
                </TableCell>
                <TableCell className="text-[12px] font-semibold text-foreground py-2.5" onClick={() => navigate(`/leads/${lead.id}`)}>{lead.student_name}</TableCell>
                <TableCell className="py-2.5" onClick={() => navigate(`/leads/${lead.id}`)}>
                  <div className="text-[12px] text-foreground/80">{lead.parent_name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{lead.parent_phone}</div>
                </TableCell>
                <TableCell className="py-2.5" onClick={() => navigate(`/leads/${lead.id}`)}>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: STAGE_ACCENT[lead.stage] || '#808080' }} />
                    {lead.stage?.replace('_', ' ')}
                  </span>
                </TableCell>
                <TableCell className="py-2.5" onClick={() => navigate(`/leads/${lead.id}`)}>
                  <div className="flex items-center gap-1.5">
                    <div className="score-bar w-10">
                      <div className={`score-bar-fill ${lead.lead_score >= 80 ? 'bg-emerald-500' : lead.lead_score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{width: `${lead.lead_score}%`}} />
                    </div>
                    <span className="text-[10px] text-muted-foreground font-mono">{lead.lead_score}</span>
                  </div>
                </TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5 font-mono" onClick={() => navigate(`/leads/${lead.id}`)}>{lead.source}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground py-2.5" onClick={() => navigate(`/leads/${lead.id}`)}>{lead.target_exam}</TableCell>
                <TableCell className="text-[10px] text-muted-foreground py-2.5 font-mono" onClick={() => navigate(`/leads/${lead.id}`)}>
                  {lead.inquiry_date ? new Date(lead.inquiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function BulkTransferDialog({ leadIds, onClose, onTransferred }) {
  const [branches, setBranches] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [counselorId, setCounselorId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/api/branches').then(r => setBranches(r.data.branches || r.data || [])).catch(console.error);
  }, []);

  useEffect(() => {
    if (!branchId) { setCounselors([]); setCounselorId(''); return; }
    api.get(`/api/users?branch_id=${branchId}&role=counselor`).then(r => setCounselors(r.data.users || r.data || [])).catch(console.error);
    setCounselorId('');
  }, [branchId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branchId) { toast.error('Select a branch'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.put('/api/leads/transfer', {
        lead_ids: leadIds,
        branch_id: branchId,
        counselor_id: counselorId,
        reason,
      });
      toast.success(`Transferred ${data.transferred} lead(s)`);
      onTransferred();
    } catch (err) {
      toast.error('Transfer failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="bulk-transfer-dialog">
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold text-foreground">Transfer {leadIds.length} Lead{leadIds.length > 1 ? 's' : ''}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Branch *</label>
            <select value={branchId} onChange={e => setBranchId(e.target.value)} data-testid="bulk-transfer-branch-select"
              className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono" required>
              <option value="">Select branch...</option>
              {branches.map(b => <option key={b.id || b._id} value={b.id || b._id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Counselor</label>
            <select value={counselorId} onChange={e => setCounselorId(e.target.value)} data-testid="bulk-transfer-counselor-select"
              className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono">
              <option value="">Auto-assign (round robin)</option>
              {counselors.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Reason</label>
            <Input value={reason} onChange={e => setReason(e.target.value)} data-testid="bulk-transfer-reason" placeholder="Reason for transfer..." className="h-9 rounded-lg text-xs" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="h-8 rounded-lg text-xs">Cancel</Button>
            <Button type="submit" size="sm" disabled={submitting} data-testid="bulk-transfer-submit-btn"
              className="h-8 rounded-lg text-xs font-bold px-4 text-white" style={{ backgroundColor: '#2563EB' }}>
              {submitting ? 'Transferring...' : `Transfer ${leadIds.length} Lead${leadIds.length > 1 ? 's' : ''}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
